import React, { useState } from 'react';
import { Button, Form, Icon, Label, Message, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import {
    IAdminBillingCompany,
    IAdminCompanyBillingUpdate,
    IAdminExtendRequest,
    IAnnualPlanRequest,
} from '../../app/interfaces/IBilling';
import { describeDuration, describeRemaining, formatDate, formatMoney, parseMoneyToCents } from '../../utils/Billing';

/*
* One company in the billing table, with its editor underneath.
*
* An empty price or length box means "follow the account default" - which is
* different from typing the default's number in, because the company then moves
* whenever the default moves. The placeholders show what the default currently
* is so that difference is visible rather than folklore.
*/
interface ICompanyBillingRowProps {
    company: IAdminBillingCompany;
    saving: boolean;
    expanded: boolean;
    onToggle: () => void;
    onSave: (update: IAdminCompanyBillingUpdate) => void;
    onExtend: (request: IAdminExtendRequest) => void;
    onStartTrial: () => void;
    onShowHistory: () => void;
    onSetAnnualPlan: (request: IAnnualPlanRequest) => void;
    onCancelAnnualPlan: (immediate: boolean) => void;
}

const CompanyBillingRow = (props: ICompanyBillingRowProps) => {
    const { company, saving, expanded } = props;

    const [priceOverride, setPriceOverride] = useState(
        company.overrides.extensionPriceCents === null
            ? ''
            : formatMoney(company.overrides.extensionPriceCents, company.effective.currency),
    );
    const [daysOverride, setDaysOverride] = useState(
        company.overrides.extensionDays === null ? '' : String(company.overrides.extensionDays),
    );
    const [trialOverride, setTrialOverride] = useState(
        company.overrides.freeTrialDays === null ? '' : String(company.overrides.freeTrialDays),
    );
    const [notes, setNotes] = useState(company.billingNotes);
    const [extendDays, setExtendDays] = useState('');
    const [extendUntil, setExtendUntil] = useState('');
    const [extendNote, setExtendNote] = useState('');
    const [annualPrice, setAnnualPrice] = useState(
        company.annual.priceCents === null
            ? ''
            : formatMoney(company.annual.priceCents, company.effective.currency),
    );
    const [annualStart, setAnnualStart] = useState('');
    const [annualTerms, setAnnualTerms] = useState(String(company.annual.daysUntilDue));
    const [annualNote, setAnnualNote] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    const savePlan = () => {
        const price = priceOverride.trim() === '' ? null : parseMoneyToCents(priceOverride);
        if (priceOverride.trim() !== '' && price === null) {
            setValidationError('Price must be a number, or blank to use the default.');
            return;
        }

        const asDays = (value: string): number | null | undefined => {
            if (value.trim() === '') {
                return null;
            }
            const parsed = parseInt(value, 10);
            return Number.isNaN(parsed) || parsed < 0 ? undefined : parsed;
        };

        const days = asDays(daysOverride);
        const trial = asDays(trialOverride);
        if (days === undefined || trial === undefined) {
            setValidationError('Lengths must be whole numbers of days, or blank to use the default.');
            return;
        }

        setValidationError(null);
        props.onSave({
            extensionPriceCents: price,
            extensionDays: days,
            freeTrialDays: trial,
            billingNotes: notes,
        });
    };

    const extend = () => {
        if (extendUntil) {
            setValidationError(null);
            props.onExtend({ expiration: extendUntil, note: extendNote });
            setExtendUntil('');
            setExtendNote('');
            return;
        }

        const days = parseInt(extendDays, 10);
        if (Number.isNaN(days) || days === 0) {
            setValidationError('Enter a number of days to add, or pick an exact end date.');
            return;
        }

        setValidationError(null);
        props.onExtend({ days, note: extendNote });
        setExtendDays('');
        setExtendNote('');
    };

    const saveAnnualPlan = () => {
        const priceCents = parseMoneyToCents(annualPrice);
        if (priceCents === null || priceCents === 0) {
            setValidationError('An annual price is required, for example 12000.');
            return;
        }

        const terms = parseInt(annualTerms, 10);
        if (Number.isNaN(terms) || terms < 0) {
            setValidationError('Payment terms must be a whole number of days.');
            return;
        }

        setValidationError(null);
        props.onSetAnnualPlan({
            priceCents,
            daysUntilDue: terms,
            startDate: annualStart || undefined,
            note: annualNote || undefined,
        });
        setAnnualNote('');
    };

    const { annual } = company;

    return (
        <>
            <Table.Row warning={company.hasLapsed || annual.isPastDue} onClick={props.onToggle}>
                <Table.Cell>
                    <Icon name={expanded ? 'chevron down' : 'chevron right'} />
                    <strong>{company.name}</strong>
                    <StyledCompanyId>#{company.id}</StyledCompanyId>
                </Table.Cell>
                <Table.Cell>
                    {company.statusLabel}
                    {company.hasLapsed && <Label color='red' size='mini' horizontal>lapsed</Label>}
                </Table.Cell>
                <Table.Cell>
                    {annual.hasPlan ? formatDate(annual.renewalDate) : formatDate(company.trialExpiration)}
                </Table.Cell>
                <Table.Cell>
                    {annual.hasPlan
                        ? (annual.isPastDue ? `Grace to ${formatDate(annual.graceEndsOn)}` : 'Renews')
                        : describeRemaining(company.daysRemaining)}
                </Table.Cell>
                <Table.Cell>
                    {annual.hasPlan
                        ? <>
                            {annual.priceDisplay} / year
                            <Label color='green' size='mini' horizontal>annual</Label>
                            {annual.isPastDue &&
                                <Label color='red' size='mini' horizontal>past due</Label>
                            }
                        </>
                        : <>
                            {formatMoney(company.effective.extensionPriceCents, company.effective.currency)}
                            {' / '}
                            {describeDuration(company.effective.extensionDays)}
                            {company.isCustomPlan && <Label color='blue' size='mini' horizontal>custom</Label>}
                            {!company.effective.extensionsEnabled &&
                                <Label size='mini' horizontal>contact us</Label>
                            }
                        </>
                    }
                </Table.Cell>
                <Table.Cell textAlign='right'>{company.totalPaidDisplay}</Table.Cell>
            </Table.Row>

            {expanded &&
                <Table.Row>
                    <Table.Cell colSpan={6}>
                        <StyledEditor>
                            {validationError && <Message negative size='tiny' content={validationError} />}

                            <Form size='small'>
                                <h4>Pricing and timing</h4>
                                <StyledHint>Leave a box blank to follow the account default.</StyledHint>
                                <Form.Group widths='equal'>
                                    <Form.Input
                                        label='Extension price'
                                        placeholder={`Default: ${formatMoney(company.effective.extensionPriceCents, company.effective.currency)}`}
                                        value={priceOverride}
                                        onChange={(_, { value }) => setPriceOverride(value)}
                                    />
                                    <Form.Input
                                        label='Extension length (days)'
                                        placeholder={`Default: ${company.effective.extensionDays}`}
                                        value={daysOverride}
                                        onChange={(_, { value }) => setDaysOverride(value)}
                                    />
                                    <Form.Input
                                        label='Free trial (days)'
                                        placeholder={`Default: ${company.effective.freeTrialDays}`}
                                        value={trialOverride}
                                        onChange={(_, { value }) => setTrialOverride(value)}
                                    />
                                </Form.Group>
                                <Form.TextArea
                                    label='Internal notes'
                                    placeholder='Never shown to the company.'
                                    rows={2}
                                    value={notes}
                                    onChange={(_, { value }) => setNotes(String(value ?? ''))}
                                />
                                <Button primary size='small' loading={saving} disabled={saving} onClick={savePlan}>
                                    Save pricing
                                </Button>

                                <StyledDivider />

                                <h4>Annual agreement</h4>
                                <StyledHint>
                                    {annual.hasPlan
                                        ? `Invoiced ${annual.priceDisplay} a year, net ${annual.daysUntilDue}. A new price takes effect at the next renewal - nothing is charged or refunded mid-term.`
                                        : 'Once a deal is struck, set the annual price here. Stripe emails an invoice every year - no card is stored and nothing is charged automatically. Billing starts when their current access runs out unless you name a date.'}
                                </StyledHint>
                                {annual.isPastDue &&
                                    <Message negative size='tiny'>
                                        Invoice past due since {formatDate(annual.pastDueSince)}.
                                        Access continues until {formatDate(annual.graceEndsOn)}.
                                        {annual.lastInvoice?.invoiceUrl &&
                                            <> <a href={annual.lastInvoice.invoiceUrl} target='_blank' rel='noreferrer'>
                                                Open the invoice
                                            </a></>
                                        }
                                    </Message>
                                }
                                <Form.Group widths='equal'>
                                    <Form.Input
                                        label='Annual price'
                                        placeholder='12000'
                                        value={annualPrice}
                                        onChange={(_, { value }) => setAnnualPrice(value)}
                                    />
                                    <Form.Input
                                        label='Payment terms (days)'
                                        value={annualTerms}
                                        onChange={(_, { value }) => setAnnualTerms(value)}
                                    />
                                    <Form.Input
                                        label={annual.hasPlan ? 'Start date (ignored when repricing)' : 'Start billing on'}
                                        type='date'
                                        disabled={annual.hasPlan}
                                        value={annualStart}
                                        onChange={(_, { value }) => setAnnualStart(value)}
                                    />
                                </Form.Group>
                                <Form.Input
                                    label='Deal note'
                                    placeholder='What was agreed, and with whom'
                                    value={annualNote}
                                    onChange={(_, { value }) => setAnnualNote(value)}
                                />
                                <Button
                                    color='green'
                                    size='small'
                                    loading={saving}
                                    disabled={saving}
                                    onClick={saveAnnualPlan}
                                >
                                    {annual.hasPlan ? 'Update annual price' : 'Start annual plan'}
                                </Button>
                                {annual.hasPlan &&
                                    <>
                                        <Button size='small' basic onClick={() => props.onCancelAnnualPlan(false)}>
                                            End at renewal
                                        </Button>
                                        <Button size='small' basic negative onClick={() => props.onCancelAnnualPlan(true)}>
                                            End now
                                        </Button>
                                    </>
                                }
                                {annual.lastInvoice &&
                                    <StyledHint>
                                        Last invoice {annual.lastInvoice.invoiceNumber || ''}
                                        {' - '}{annual.lastInvoice.kindLabel.toLowerCase()}
                                        {annual.lastInvoice.invoiceDueDate &&
                                            <>, due {formatDate(annual.lastInvoice.invoiceDueDate)}</>
                                        }
                                        {annual.lastInvoice.invoiceUrl &&
                                            <> · <a href={annual.lastInvoice.invoiceUrl} target='_blank' rel='noreferrer'>
                                                view
                                            </a></>
                                        }
                                    </StyledHint>
                                }

                                <StyledDivider />

                                <h4>Adjust their time</h4>
                                <StyledHint>
                                    Adds to whatever is left, or sets an exact end date. Recorded as a
                                    comped extension - use the Stripe checkout on their own page to charge for one.
                                </StyledHint>
                                <Form.Group widths='equal'>
                                    <Form.Input
                                        label='Add days'
                                        placeholder={String(company.effective.extensionDays)}
                                        value={extendDays}
                                        onChange={(_, { value }) => setExtendDays(value)}
                                    />
                                    <Form.Input
                                        label='Or end on'
                                        type='date'
                                        value={extendUntil}
                                        onChange={(_, { value }) => setExtendUntil(value)}
                                    />
                                    <Form.Input
                                        label='Reason'
                                        placeholder='Why they are getting this'
                                        value={extendNote}
                                        onChange={(_, { value }) => setExtendNote(value)}
                                    />
                                </Form.Group>
                                <Button size='small' loading={saving} disabled={saving} onClick={extend}>
                                    Apply
                                </Button>
                                <Button size='small' basic onClick={props.onStartTrial}>
                                    Restart free trial
                                </Button>
                                <Button size='small' basic onClick={props.onShowHistory}>
                                    Billing history
                                </Button>
                            </Form>

                            <StyledStripeIds>
                                {company.stripeCustomerId
                                    ? <>Stripe customer <code>{company.stripeCustomerId}</code></>
                                    : <>No Stripe customer yet - one is created the first time they pay.</>}
                                {company.stripeSubscriptionId &&
                                    <> · legacy subscription <code>{company.stripeSubscriptionId}</code></>
                                }
                                {company.lastPaymentAt && <> · last payment {formatDate(company.lastPaymentAt)}</>}
                            </StyledStripeIds>
                        </StyledEditor>
                    </Table.Cell>
                </Table.Row>
            }
        </>
    );
};

const StyledCompanyId = styled('span')`
    color: rgba(0, 0, 0, 0.4);
    margin-left: 8px;
`;

const StyledEditor = styled('div')`
    background: #fafafa;
    border-radius: 4px;
    padding: 16px;
    & h4 {
        margin-bottom: 4px;
    }
`;

const StyledHint = styled('p')`
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.9em;
    margin-bottom: 12px;
`;

const StyledDivider = styled('hr')`
    border: none;
    border-top: 1px solid rgba(34, 36, 38, 0.15);
    margin: 20px 0;
`;

const StyledStripeIds = styled('div')`
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.85em;
    margin-top: 16px;
`;

export default CompanyBillingRow;
